import { useState, useEffect, useCallback } from 'react';
import { API, LEVEL_BANDS, formatAmount, authFetch } from '../../constants';
import { useApp } from '../../contexts/AppContext';
import { useToast } from '../../contexts/ToastContext';
import SILBreakdown from '../SILBreakdown';
import AuditTrail from '../AuditTrail';
import { ResolutionPanelProps, ESCALATION_LEVELS } from './types';
import InvestigationPanel from './InvestigationPanel';
import ResolutionOptions from './ResolutionOptions';

export default function ResolutionPanel({ exceptionId, onClose, onResolved }: ResolutionPanelProps) {
  const { toast } = useToast();
  const [detail, setDetail] = useState<any>(null);
  const [riskData, setRiskData] = useState<any>(null);
  const [resolution, setResolution] = useState<any>(null);
  const [loadingResolution, setLoadingResolution] = useState(false);
  const [executingOption, setExecutingOption] = useState<string | null>(null);
  const [resolveStatus, setResolveStatus] = useState<{ ok: boolean; msg: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [auditData, setAuditData] = useState<any>(null);
  // Investigation workflow state
  const [investigatingOption, setInvestigatingOption] = useState<string | null>(null);
  const [investigationNotes, setInvestigationNotes] = useState('');
  const [investigationSubmitted, setInvestigationSubmitted] = useState<Record<string, boolean>>({});
  const { role, authUser } = useApp();
  const currentUserId = authUser?.id || 1;

  // Permissions from backend API
  const getPerms = () => detail?.exception?.permissions || null;

  const canApprove = (_amount: number): boolean => {
    const perms = getPerms();
    if (perms) return perms.can_approve;
    if (role.level === 'L6') return true;
    return false;
  };

  const getBlockedReason = (): string | null => {
    const perms = getPerms();
    return perms?.approval_blocked_reason || null;
  };

  const canEscalate = (): boolean => {
    const perms = getPerms();
    if (perms) return perms.can_escalate;
    return true;
  };

  const getEscalationTarget = (): string | null => {
    const exc = detail?.exception;
    const currentLevel = exc?.escalation_level || 'L2';
    const num = parseInt(currentLevel.replace('L', ''));
    if (isNaN(num) || num >= 7) return null;
    return `L${num + 1}`;
  };

  const loadDetail = useCallback(async () => {
    setLoading(true);
    try {
      const roleDept = Array.isArray(role.departments) ? role.departments[0] || '' : 'all';
      const permParams = `?agent_level=${role.level}&agent_department=${encodeURIComponent(roleDept)}`;
      const [detailRes, riskRes] = await Promise.all([
        authFetch(`${API}/${exceptionId}${permParams}`),
        authFetch(`${API}/${exceptionId}/risk-score`),
      ]);
      const detailData = await detailRes.json();
      setDetail(detailData);

      const results = detailData.results || [];
      const latest = results.length > 0 ? results[results.length - 1] : null;
      if (latest?.recommendation_detail?.resolution_version) {
        setResolution(latest.recommendation_detail);
      } else {
        setResolution(null);
      }

      if (detailData.resolution_audit) {
        setAuditData(detailData.resolution_audit);
      } else if (latest?.recommendation_detail?.audit) {
        setAuditData(latest.recommendation_detail.audit);
      }

      if (riskRes.ok) {
        setRiskData(await riskRes.json());
      }
    } catch (err) {
      console.error('Failed to load detail:', err);
      toast({ message: 'Failed to load exception details.', type: 'error' });
    }
    setLoading(false);
  }, [exceptionId, role]);

  useEffect(() => { loadDetail(); }, [loadDetail]);

  // Auto-generate resolution if none exists and not resolved
  useEffect(() => {
    if (!loading && detail && !resolution && !loadingResolution) {
      const exc = detail.exception || {};
      const status = (exc.status || '').toLowerCase();
      if (!['resolved_auto', 'resolved_human', 'dismissed'].includes(status)) {
        generateResolution();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, detail]);

  // force=true bypasses the resolution_router cache. Used by the
  // "Regenerate" button so a reviewer can re-run after a code/model
  // fix without waiting for the cache to age out. Cached fallback
  // templates from before a deploy are otherwise sticky (2026-05-22:
  // Albert saw old "Unable to determine root cause" panels on every
  // exception even after the diagnostic-surfacing patch shipped --
  // they were cached from before the deploy).
  const generateResolution = async (force: boolean = false) => {
    setLoadingResolution(true);
    try {
      const url = force
        ? `${API}/${exceptionId}/generate-resolution?force=true`
        : `${API}/${exceptionId}/generate-resolution`;
      const res = await authFetch(url, { method: 'POST' });
      const data = await res.json();
      if (data.resolution_version || data.diagnosis) {
        setResolution(data);
        if (data.audit) {
          setAuditData(data.audit);
        }
      }
    } catch (err) {
      console.error('Resolution generation failed:', err);
      toast({ message: 'Resolution generation failed. Please try again.', type: 'error' });
    }
    setLoadingResolution(false);
  };

  const executeResolution = async (optionId: string, action: string, optionLabel?: string, optionDesc?: string) => {
    setExecutingOption(optionId);
    setResolveStatus(null);
    try {
      const res = await authFetch(`${API}/${exceptionId}/resolve`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action,
          notes: `${action === 'approved' ? 'Approved' : action === 'modified' ? 'Modified' : 'Rejected'} via Agent View: ${optionLabel || optionId}`,
          user_id: currentUserId,
          role_level: role.level,
          approval_limit: LEVEL_BANDS[role.level]?.ceiling || 0,
          resolution_summary: optionDesc || optionLabel || optionId,
          option_id: optionId,
          resolution_result_id: latestResultId,
        }),
      });
      if (res.ok) {
        setResolveStatus({ ok: true, msg: 'Exception resolved successfully' });
        await loadDetail();
        onResolved();
      } else {
        const err = await res.json().catch(() => ({ detail: 'Unknown error' }));
        setResolveStatus({ ok: false, msg: err.detail || 'Resolution failed' });
      }
    } catch (err) {
      setResolveStatus({ ok: false, msg: 'Network error — could not reach server' });
    }
    setExecutingOption(null);
  };

  const handleEscalateToLevel = async (level: string) => {
    setResolveStatus(null);
    try {
      const res = await authFetch(`${API}/${exceptionId}/escalate-to/${level}?reason=Manual+escalation+from+Agent+View`, { method: 'POST' });
      if (res.ok) {
        setResolveStatus({ ok: true, msg: `Escalated to ${level}` });
        await loadDetail();
        onResolved();
      } else {
        const err = await res.json().catch(() => ({ detail: 'Escalation failed' }));
        setResolveStatus({ ok: false, msg: err.detail || 'Escalation failed' });
      }
    } catch (err) {
      setResolveStatus({ ok: false, msg: 'Network error — could not escalate' });
    }
  };

  const handleEscalate = async () => {
    setResolveStatus(null);
    try {
      const res = await authFetch(`${API}/${exceptionId}/escalate`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: 'Escalated from Agent View', user_id: 1 }),
      });
      if (res.ok) {
        const data = await res.json();
        setResolveStatus({ ok: true, msg: data.message || 'Escalated to next level' });
        await loadDetail();
        onResolved();
      } else {
        const err = await res.json().catch(() => ({ detail: 'Escalation failed' }));
        setResolveStatus({ ok: false, msg: err.detail || 'Escalation failed' });
      }
    } catch (err) {
      setResolveStatus({ ok: false, msg: 'Network error — could not escalate' });
    }
  };

  const startInvestigation = async (optionId: string) => {
    if (!investigationNotes.trim() || investigationNotes.trim().length < 10) {
      setResolveStatus({ ok: false, msg: 'Investigation notes required (min 10 characters) — SOX compliance' });
      return;
    }
    setExecutingOption(optionId);
    setResolveStatus(null);
    try {
      const res = await authFetch(`${API}/${exceptionId}/investigate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          notes: investigationNotes.trim(),
          option_id: optionId,
          user_id: currentUserId,
        }),
      });
      if (res.ok) {
        setInvestigationSubmitted(prev => ({ ...prev, [optionId]: true }));
        setResolveStatus({ ok: true, msg: 'Investigation started — review findings below' });
        await loadDetail();
      } else {
        const err = await res.json().catch(() => ({ detail: 'Unknown error' }));
        setResolveStatus({ ok: false, msg: err.detail || 'Failed to start investigation' });
      }
    } catch (err) {
      setResolveStatus({ ok: false, msg: 'Network error' });
    }
    setExecutingOption(null);
  };

  const approveAfterInvestigation = async (optionId: string, optionLabel?: string, optionDesc?: string) => {
    setExecutingOption(optionId);
    setResolveStatus(null);
    try {
      const res = await authFetch(`${API}/${exceptionId}/approve-after-investigation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          notes: investigationNotes.trim(),
          option_id: optionId,
          user_id: currentUserId,
          role_level: role.level,
          resolution_summary: `Approved after investigation: ${optionLabel || optionId}. ${optionDesc || ''}`.slice(0, 500),
          resolution_result_id: latestResultId,
        }),
      });
      if (res.ok) {
        setResolveStatus({ ok: true, msg: 'Approved after investigation — audit trail updated' });
        await loadDetail();
        onResolved();
      } else {
        const err = await res.json().catch(() => ({ detail: 'Unknown error' }));
        setResolveStatus({ ok: false, msg: err.detail || 'Approval failed' });
      }
    } catch (err) {
      setResolveStatus({ ok: false, msg: 'Network error' });
    }
    setExecutingOption(null);
  };

  if (loading) {
    return (
      <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-dim)' }}>
        Loading exception detail...
      </div>
    );
  }

  if (!detail) return null;

  const exc = detail.exception || {};
  const audit = detail.audit_trail || [];
  const confidenceData = detail.confidence_breakdown;
  const signals = confidenceData?.breakdown || (detail.intelligence_signals || []).map((s: any) => ({
    signal_type: s.signal_type, score: s.score, weight: s.weight,
  }));
  const statusKey = (exc.status || 'detected').toLowerCase();
  const isResolved = ['resolved_auto', 'resolved_human', 'dismissed'].includes(statusKey);

  const isEngineMetadata = (text: string) =>
    /workflow skill:|Matching complete:|Detected by workflow|exceptions out of|records scanned/i.test(text);
  const formatExceptionType = (t: string) => (t || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  const displayTitle = (exc.title && !isEngineMetadata(exc.title))
    ? exc.title
    : formatExceptionType(exc.exception_type);

  const results = detail.results || [];
  const latestResult = results.length > 0 ? results[results.length - 1] : null;
  const latestResultId = latestResult?.id || null;

  const excAmount = Number(exc.amount || 0);
  const userCanApprove = canApprove(excAmount);
  const blockedReason = getBlockedReason();
  const escalationTarget = getEscalationTarget();

  return (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--bg-border)', borderRadius: 12, overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--bg-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-dimmer)', letterSpacing: 0.5 }}>RESOLUTION PANEL</span>
          <span style={{ fontSize: 10, color: 'var(--text-dimmer)', marginLeft: 8 }}>#{exc.id}</span>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            type="button"
            onClick={() => generateResolution(true)}
            disabled={loadingResolution}
            title="Bypass cache and re-run AI analysis"
            style={{
              background: 'var(--accent-subtle)',
              border: '1px solid var(--accent-border)',
              color: 'var(--accent)',
              cursor: loadingResolution ? 'wait' : 'pointer',
              fontSize: 10, fontWeight: 700,
              padding: '4px 10px', borderRadius: 4,
              opacity: loadingResolution ? 0.5 : 1,
            }}
          >{loadingResolution ? 'Regenerating...' : 'Regenerate'}</button>
          <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-dim)', cursor: 'pointer', fontSize: 14 }}>X</button>
        </div>
      </div>

      <div style={{ padding: 16, maxHeight: 600, overflowY: 'auto' }}>
        {/* Exception Summary */}
        <div style={{ marginBottom: 16, padding: 14, background: 'rgba(0,0,0,0.02)', borderRadius: 8 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 6 }}>{displayTitle}</div>
          {exc.description && !isEngineMetadata(exc.description) && <div style={{ fontSize: 11, color: 'var(--text-dim)', lineHeight: 1.6, marginBottom: 8 }}>{exc.description}</div>}
          <div style={{ display: 'flex', gap: 16, fontSize: 11 }}>
            {exc.amount != null && <span><span style={{ color: 'var(--text-dimmer)' }}>Amount: </span><span className="mono" style={{ fontWeight: 700 }}>{formatAmount(Number(exc.amount))}</span></span>}
            {exc.vendor_name && <span><span style={{ color: 'var(--text-dimmer)' }}>Vendor: </span>{exc.vendor_name}</span>}
          </div>
        </div>

        {/* D14: Workflow Source Banner */}
        {exc.source_workflow_instance_id && (
          <div style={{ marginBottom: 16, padding: '10px 14px', background: 'var(--accent-subtle)', border: '1px solid var(--accent-border)', borderRadius: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <span style={{ fontSize: 9, fontWeight: 600, letterSpacing: 0.5, padding: '2px 8px', background: 'var(--accent-subtle)', color: 'var(--navy-800)', borderRadius: 4 }}>WORKFLOW SOURCE</span>
              <span className="mono" style={{ fontSize: 10, color: 'var(--navy-800)', fontWeight: 700 }}>Instance #{exc.source_workflow_instance_id}</span>
            </div>
            <div style={{ display: 'flex', gap: 16, fontSize: 10 }}>
              {exc.source_skill_code && (
                <span><span style={{ color: 'var(--text-dimmer)' }}>Skill: </span><span style={{ color: 'var(--text-dim)' }}>{exc.source_skill_code.replace(/_/g, ' ')}</span></span>
              )}
              {exc.source_transaction_id && (
                <span><span style={{ color: 'var(--text-dimmer)' }}>Transaction: </span><span className="mono" style={{ color: 'var(--text-dim)' }}>{exc.source_transaction_id}</span></span>
              )}
            </div>
          </div>
        )}

        {/* Confidence + Risk Bars */}
        {(confidenceData || riskData) && (
          <div style={{ marginBottom: 16, padding: 14, background: 'rgba(0,0,0,0.02)', borderRadius: 8 }}>
            {confidenceData && (
              <div style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <span style={{ fontSize: 10, color: 'var(--text-dim)', fontWeight: 600 }}>CONFIDENCE</span>
                  <span className="mono" style={{ fontSize: 16, fontWeight: 600, color: confidenceData.overall_score >= 0.85 ? 'var(--accent-good)' : confidenceData.overall_score >= 0.6 ? 'var(--accent-warning)' : 'var(--accent-critical)' }}>
                    {(confidenceData.overall_score * 100).toFixed(0)}%
                  </span>
                </div>
                <div style={{ height: 8, background: 'rgba(0,0,0,0.05)', borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{ width: `${confidenceData.overall_score * 100}%`, height: '100%', background: confidenceData.overall_score >= 0.85 ? 'var(--accent-good)' : confidenceData.overall_score >= 0.6 ? 'var(--accent-warning)' : 'var(--accent-critical)', borderRadius: 4, transition: 'width 0.3s' }} />
                </div>
              </div>
            )}
            {riskData?.risk && (
              <div style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <span style={{ fontSize: 10, color: 'var(--text-dim)', fontWeight: 600 }}>RISK SCORE</span>
                  <span className="mono" style={{ fontSize: 16, fontWeight: 600, color: riskData.risk.score < 25 ? 'var(--accent-good)' : riskData.risk.score < 50 ? 'var(--accent-warning)' : 'var(--accent-critical)' }}>
                    {riskData.risk.score.toFixed(0)} <span style={{ fontSize: 10, fontWeight: 600 }}>{riskData.risk.level}</span>
                  </span>
                </div>
                <div style={{ height: 8, background: 'rgba(0,0,0,0.05)', borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{ width: `${riskData.risk.score}%`, height: '100%', background: riskData.risk.score < 25 ? 'var(--accent-good)' : riskData.risk.score < 50 ? 'var(--accent-warning)' : 'var(--accent-critical)', borderRadius: 4 }} />
                </div>
              </div>
            )}
            {riskData?.routing && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: riskData.routing.action === 'AUTO_RESOLVE' ? 'rgba(0,212,170,0.1)' : riskData.routing.action === 'HYBRID' ? 'var(--accent-subtle)' : 'var(--accent-subtle)', borderRadius: 6, marginTop: 4 }}>
                <span style={{ fontSize: 10, fontWeight: 600, color: riskData.routing.action === 'AUTO_RESOLVE' ? 'var(--accent-good)' : riskData.routing.action === 'HYBRID' ? 'var(--accent-info)' : 'var(--navy-800)', letterSpacing: 0.5 }}>
                  {riskData.routing.action.replace(/_/g, ' ')}
                </span>
                {(() => {
                  const triageLevel = riskData.routing.level;
                  const currentLevel = exc.escalation_level || triageLevel;
                  const wasEscalated = currentLevel && triageLevel && currentLevel !== triageLevel;
                  const lvlCfg = ESCALATION_LEVELS[currentLevel] || ESCALATION_LEVELS.L2;
                  return currentLevel ? (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span style={{ fontSize: 9, padding: '1px 6px', background: lvlCfg.bg, color: lvlCfg.color, borderRadius: 3, fontWeight: 700 }}>
                        {currentLevel}
                      </span>
                      {wasEscalated && (
                        <span style={{ fontSize: 8, color: 'var(--text-dimmer)' }}>
                          from {triageLevel}
                        </span>
                      )}
                    </span>
                  ) : null;
                })()}
                <span style={{ fontSize: 9, color: 'var(--text-dimmer)', flex: 1, textAlign: 'right' }}>
                  {riskData.routing.requires_human ? 'Human approval needed' : 'No human needed'}
                </span>
              </div>
            )}
          </div>
        )}

        {/* AI Resolution content */}
        {resolution ? (
          <>
            <InvestigationPanel
              resolution={resolution}
              detail={detail}
              auditData={auditData}
              isResolved={isResolved}
              exc={exc}
              loadingResolution={loadingResolution}
              onGenerateResolution={generateResolution}
            />

            {/* Resolution Options */}
            <ResolutionOptions
              resolution={resolution}
              exc={exc}
              isResolved={isResolved}
              auditData={auditData}
              userCanApprove={userCanApprove}
              blockedReason={blockedReason}
              escalationTarget={escalationTarget}
              executingOption={executingOption}
              investigatingOption={investigatingOption}
              investigationNotes={investigationNotes}
              investigationSubmitted={investigationSubmitted}
              statusKey={statusKey}
              onExecuteResolution={executeResolution}
              onEscalateToLevel={handleEscalateToLevel}
              onEscalate={handleEscalate}
              onStartInvestigation={startInvestigation}
              onApproveAfterInvestigation={approveAfterInvestigation}
              onSetInvestigatingOption={setInvestigatingOption}
              onSetInvestigationNotes={setInvestigationNotes}
            />
          </>
        ) : (
          <InvestigationPanel
            resolution={null}
            detail={detail}
            auditData={auditData}
            isResolved={isResolved}
            exc={exc}
            loadingResolution={loadingResolution}
            onGenerateResolution={generateResolution}
          />
        )}

        {/* Standalone ESCALATE button */}
        {!isResolved && canEscalate() && (() => {
          const target = getEscalationTarget();
          return target ? (
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={() => handleEscalateToLevel(target)} style={{
                padding: '8px 20px', background: 'var(--accent-subtle)',
                border: '1px solid rgba(139,92,246,0.3)', borderRadius: 6,
                color: 'var(--navy-800)', fontWeight: 700, fontSize: 10, cursor: 'pointer',
                letterSpacing: 0.5,
              }}>
                ESCALATE TO {target}
              </button>
            </div>
          ) : null;
        })()}

        {/* Resolve feedback */}
        {resolveStatus && (
          <div style={{ marginBottom: 16, padding: '12px 16px', background: resolveStatus.ok ? 'rgba(0,212,170,0.1)' : 'rgba(239,68,68,0.1)', border: `1px solid ${resolveStatus.ok ? 'rgba(0,212,170,0.3)' : 'rgba(239,68,68,0.3)'}`, borderRadius: 8, textAlign: 'center' }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: resolveStatus.ok ? 'var(--accent-good)' : 'var(--accent-critical)' }}>
              {resolveStatus.ok ? 'RESOLVED' : 'ERROR'}: {resolveStatus.msg}
            </span>
          </div>
        )}

        {/* Resolution status */}
        {exc.resolution_action && (
          <div style={{ marginBottom: 16, padding: '12px 16px', background: 'rgba(0,212,170,0.08)', borderRadius: 8, textAlign: 'center' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--accent-good)' }}>RESOLVED</div>
            {exc.resolution_summary && <div style={{ fontSize: 10, color: 'var(--text-dim)', marginTop: 4 }}>{exc.resolution_summary}</div>}
          </div>
        )}

        {/* SIL Breakdown */}
        {signals.length > 0 && (
          <SILBreakdown
            signals={signals}
            composite={confidenceData?.overall_score || 0}
            risk={riskData?.risk?.score}
          />
        )}

        {/* Audit Trail */}
        <AuditTrail entries={audit} />
      </div>
    </div>
  );
}
